import React, { CSSProperties, use, useMemo } from "react";
import { Button, Input, Radio, Space, Tooltip } from "antd";
import { Kbd } from "@heroui/kbd";
import { FormattedMessage } from "react-intl";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

import Category from "scripts/models/Category";
import Creditor, { Debitor } from "scripts/models/Creditor";
import Tag from "scripts/models/Tag";
import { Base } from "@binale-tech/shared";
import { BrowserDetection } from "scripts/infrastructure/helpers/browser";
import { CategoryUtils } from "../../../scripts/models/utils/CategoryUtils";
import { CompanyContext } from "scripts/context/CompanyContext";
import { KontoContext } from "scripts/context/accountingData/KontoEntitiesProvider";
import { TableViewContext } from "../../../scripts/context/accountingTable/tableViewContext";
import { useCategoriesByYearMonth } from "scripts/infrastructure/hooks";
import "./LeftTable.css";
import { CategoryCreditorMode, CategoryCreditorModes } from "../../../scripts/core/Product";
import { VirtualTable } from "@app/components/shared/Table2/VirtualTable";
import { clsx } from "clsx";

interface LeftTableOwnProps {
    show: SidePanels;
    onClickCategory: (v: Category) => void;
    onClickCreditor: (v: Creditor) => void;
    onClickDebitor: (v: Debitor) => void;
    onClickTag: (v: Tag) => void;
}

interface LeftTableStateProps {
    kontoExt?: number;
    skr?: number;
    allCategories: Category[];
    userCategories: Category[];
    creditors?: Base.IExtNum[];
    debitors?: Base.IExtNum[];
    tags?: Tag[];
}

declare type CatMode = "all" | "user";
const MODE_ALL: CatMode = "all";
const MODE_USER: CatMode = "user";

enum SidePanels {
    SIDE_CATEGORY = "category",
    SIDE_TAG = "tag",
    SIDE_CREDITOR = "creditor",
    SIDE_DEBITOR = "debitor",
}

const InternalLeftTable: React.FC<LeftTableOwnProps & LeftTableStateProps> = props => {
    const [searchTerm, setSearchTerm] = React.useState("");
    const [searchTermDisplay, setSearchTermDisplay] = React.useState("");
    const [mode, setMode] = React.useState<CatMode>(MODE_USER);

    const subjectRef = React.useRef<Subject<string>>(new Subject<string>());
    const subscriptionRef = React.useRef<Subscription | null>(null);

    // Define callback functions
    const handleChangeInput = React.useCallback<React.ComponentProps<typeof Input>["onChange"]>(e => {
        // If e is null, clear the search
        const value = e === null ? "" : e?.target?.value || "";
        setSearchTermDisplay(value);
        subjectRef.current.next(value);
    }, []);

    const handleSizeChange = React.useCallback((e: any) => {
        setMode(e.target.value);
    }, []);

    // Setup RxJS subscription
    React.useEffect(() => {
        subscriptionRef.current = subjectRef.current.pipe(debounceTime(250), distinctUntilChanged()).subscribe(v => {
            setSearchTerm(v.toLowerCase());
        });

        return () => {
            subscriptionRef.current?.unsubscribe();
        };
    }, []);

    // Handle changes to the show prop
    const prevShowRef = React.useRef<SidePanels | null>(null);

    React.useEffect(() => {
        // If show was previously null/undefined and now has a value
        if (!prevShowRef.current && props.show) {
            setMode(MODE_ALL);
        }

        // If show has changed
        if (prevShowRef.current !== props.show) {
            handleChangeInput(null);
        }

        // Update the ref with current value for next comparison
        prevShowRef.current = props.show;
    }, [props.show, handleChangeInput]);

    if (!props.show) {
        return null;
    }

    const getCategories = () => {
        return mode === MODE_USER ? props.userCategories : props.allCategories;
    };

    const getFiltered = (input: Base.INumName[]) => {
        if (!searchTerm || input.length === 0) {
            return input;
        }
        const fNum = input.filter(v => v.num.startsWith(searchTerm));
        if (fNum.length > 0) {
            return fNum;
        }
        return input.filter(v => {
            if (!v.name) {
                return false;
            }
            return v.name.toLowerCase().includes(searchTerm);
        });
    };

    const getTable = (
        data: Base.INumName[],
        onClick: (v: Base.INumName) => void,
        isDisabled?: (v: Base.INumName) => boolean
    ) => {
        return (
            <div className="NumNameTable">
                <VirtualTable<Base.IExtNum>
                    itemHeight={25}
                    data={data as Base.IExtNum[]}
                    rowDefinition={{
                        getRowClassNames: item => {
                            return clsx("NumNameTable__Row", {
                                "NumNameTable__Row--disabled": isDisabled?.(item),
                            });
                        },
                        isRowClickable: item => {
                            return !isDisabled?.(item);
                        },
                        onRowClick: item => {
                            if (isDisabled?.(item)) {
                                return;
                            }
                            onClick?.(item);
                        },
                    }}
                    columnDefinitions={[
                        {
                            key: "konto",
                            headers: [<FormattedMessage key={"konto-header"} id="app.fields.konto" />],
                            width: 60 + props.kontoExt * 5,
                            render: item => {
                                return item.getExtNumPrint(props.kontoExt);
                            },
                        },
                        {
                            key: "description",
                            headers: [<FormattedMessage key={"konto-header"} id="app.fields.bezeichnung" />],
                            width: "*",
                            ellipsis: true,
                            render: item => {
                                const num = item.getExtNumPrint(props.kontoExt);
                                return (
                                    <Tooltip
                                        destroyOnHidden
                                        placement="topLeft"
                                        title={
                                            <Space direction="vertical">
                                                <Kbd>{num}</Kbd>
                                                <div>{item.name}</div>
                                            </Space>
                                        }
                                    >
                                        {item.name}
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                />
            </div>
        );
    };

    const getCategoryTable = () => {
        const data = getFiltered(getCategories());
        return getTable(data, props.onClickCategory, v =>
            CategoryUtils.isAggregationCategory(v as Base.IExtNum, props.skr)
        );
    };

    const getCreditorTable = () => {
        const rows = getFiltered(props.creditors);
        return getTable(rows, props.onClickCreditor);
    };

    const getDebitorTable = () => {
        const rows = getFiltered(props.debitors);
        return getTable(rows, props.onClickDebitor);
    };

    const getTagTable = () => {
        const rows = getFiltered(props.tags);
        return getTable(rows, props.onClickTag);
    };

    const getItemsTable = () => {
        if (props.show === SidePanels.SIDE_CATEGORY) {
            return getCategoryTable();
        } else if (props.show === SidePanels.SIDE_CREDITOR) {
            return getCreditorTable();
        } else if (props.show === SidePanels.SIDE_TAG) {
            return getTagTable();
        } else if (props.show === SidePanels.SIDE_DEBITOR) {
            return getDebitorTable();
        }
        return null;
    };

    const radioBlock = () => {
        const disabled = props.show !== SidePanels.SIDE_CATEGORY;
        const className = BrowserDetection.isFirefox ? "LeftTable__ButtonGroup--ff" : "";
        return (
            <Radio.Group
                value={mode}
                onChange={handleSizeChange}
                className={"LeftTable__ButtonGroup " + className}
                disabled={disabled}
            >
                <Radio.Button value={MODE_ALL}>
                    <FormattedMessage id="app.components.all" />
                </Radio.Button>
                <Radio.Button value={MODE_USER}>
                    <FormattedMessage id="app.components.used" />
                </Radio.Button>
            </Radio.Group>
        );
    };

    const searchBlock = () => {
        return <Input value={searchTermDisplay} placeholder="Suchen" onChange={handleChangeInput} />;
    };

    return (
        <div className="LeftTable">
            {radioBlock()}
            {searchBlock()}
            {getItemsTable()}
        </div>
    );
};

const LeftTable: React.FC<
    Omit<LeftTableOwnProps, "onClickTag" | "onClickCategory" | "onClickCreditor" | "onClickDebitor">
> = React.memo(({ show }) => {
    const { yearConfig } = React.use(CompanyContext);
    const { onSelectLeftTableAccount } = React.use(TableViewContext);
    const kontoExt = yearConfig?.kontoExt ?? 0;
    const skr = yearConfig?.skr;
    const { creditors, debitors, tags } = React.use(KontoContext);
    const { allCategories, userCategories } = useCategoriesByYearMonth();
    return (
        <InternalLeftTable
            show={show}
            onClickTag={tag => {
                onSelectLeftTableAccount({ tag });
            }}
            onClickDebitor={debetor => {
                onSelectLeftTableAccount({ debetor });
            }}
            onClickCreditor={creditor => {
                onSelectLeftTableAccount({ creditor });
            }}
            onClickCategory={category => {
                onSelectLeftTableAccount({ category });
            }}
            allCategories={allCategories}
            userCategories={userCategories}
            creditors={creditors}
            debitors={debitors}
            tags={tags}
            kontoExt={kontoExt}
            skr={skr}
        />
    );
});

export const LeftBlockWrapper: React.FC<React.PropsWithChildren<{ style?: CSSProperties }>> = ({ children, style }) => {
    const { programSettingsProvider } = React.use(CompanyContext);
    const { product, productKey } = use(TableViewContext);
    const productConfig = useMemo(() => product.getConfig(), [product]);
    const [sidePanel, showSidePanel] = React.useState<SidePanels | null>(null);
    const handleClick = React.useCallback((v: SidePanels) => {
        showSidePanel(showSide => {
            if (showSide && showSide === v) {
                return null;
            }
            return v;
        });
    }, []);

    const leftSideButtons = React.useMemo(() => {
        const settings = programSettingsProvider(productKey);
        const s = new Set([SidePanels.SIDE_CATEGORY]);

        const isCredSupported = (v: CategoryCreditorMode) =>
            [CategoryCreditorModes.CRED, CategoryCreditorModes.CCD, CategoryCreditorModes.RecordKontoFE].includes(v);
        const isDebSupported = (v: CategoryCreditorMode) =>
            [CategoryCreditorModes.DEB, CategoryCreditorModes.CCD, CategoryCreditorModes.RecordKontoFE].includes(v);

        if (isDebSupported(productConfig.recordAccountMode) || isDebSupported(productConfig.itemAccountMode)) {
            s.add(SidePanels.SIDE_DEBITOR);
        }
        if (isCredSupported(productConfig.recordAccountMode) || isCredSupported(productConfig.itemAccountMode)) {
            s.add(SidePanels.SIDE_CREDITOR);
        }

        if (settings.useKs) {
            s.add(SidePanels.SIDE_TAG);
        }
        return (
            <>
                {s.has(SidePanels.SIDE_CATEGORY) && (
                    <Button
                        className="LeftBlockWrapper__leftSideButton"
                        tabIndex={-1}
                        onClick={() => handleClick(SidePanels.SIDE_CATEGORY)}
                        type={sidePanel === SidePanels.SIDE_CATEGORY ? "primary" : "default"}
                    >
                        <span className="LeftBlockWrapper__leftSideButtonText">
                            <FormattedMessage id="app.titles.category" />
                        </span>
                    </Button>
                )}
                {s.has(SidePanels.SIDE_DEBITOR) && (
                    <Button
                        className="LeftBlockWrapper__leftSideButton"
                        tabIndex={-1}
                        onClick={() => handleClick(SidePanels.SIDE_DEBITOR)}
                        type={sidePanel === SidePanels.SIDE_DEBITOR ? "primary" : "default"}
                    >
                        <span className="LeftBlockWrapper__leftSideButtonText">
                            <FormattedMessage id="app.titles.debitor.pl" />
                        </span>
                    </Button>
                )}
                {s.has(SidePanels.SIDE_CREDITOR) && (
                    <Button
                        key="lsbcred"
                        className="LeftBlockWrapper__leftSideButton"
                        tabIndex={-1}
                        onClick={() => handleClick(SidePanels.SIDE_CREDITOR)}
                        type={sidePanel === SidePanels.SIDE_CREDITOR ? "primary" : "default"}
                    >
                        <span className="LeftBlockWrapper__leftSideButtonText">
                            <FormattedMessage id="app.titles.creditor.pl" />
                        </span>
                    </Button>
                )}
                {s.has(SidePanels.SIDE_TAG) && (
                    <Button
                        className="LeftBlockWrapper__leftSideButton"
                        tabIndex={-1}
                        onClick={() => handleClick(SidePanels.SIDE_TAG)}
                        type={sidePanel === SidePanels.SIDE_TAG ? "primary" : "default"}
                    >
                        <span className="LeftBlockWrapper__leftSideButtonText">
                            <FormattedMessage id="app.titles.tag.pl" />
                        </span>
                    </Button>
                )}
            </>
        );
    }, [programSettingsProvider, productKey, productConfig, sidePanel, handleClick]);

    return (
        <div className="LeftBlockWrapper LeftBlockWrapper__flexRow" style={style}>
            <div className="LeftBlockWrapper__flexCol">{leftSideButtons}</div>
            <LeftTable show={sidePanel} />
            {children}
        </div>
    );
};
